import { createFileRoute } from "@tanstack/react-router";
import { subMinutes } from "date-fns";
import { useState, useMemo } from "react";

import { SessionActionButtons } from "~/components/summit/session-action-buttons";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import { Slider } from "~/components/ui/slider";
import type { SummitSession, SummitSessionFormat } from "~/lib/summit";
import type { SessionActionState, SessionPhase } from "~/lib/summit";

export const Route = createFileRoute("/_public/summit/debug")({
  component: DebugPage,
});

const TIME_PRESETS = [
  { label: "2 hours before", minutes: -120 },
  { label: "30 min before", minutes: -30 },
  { label: "15 min before (joinable)", minutes: -15 },
  { label: "5 min before", minutes: -5 },
  { label: "Session start", minutes: 0 },
  { label: "30 min into session", minutes: 30 },
  { label: "5 min before end", minutes: 55 },
  { label: "Session ended", minutes: 65 },
] as const;

interface TestScenario {
  id: string;
  label: string;
  sessionFormat: SummitSessionFormat;
  isRegistered: boolean;
  userIsVip: boolean;
  hasRecording: boolean;
  hasSpecialOffer: boolean;
  isAtCapacity: boolean;
  limitReached: boolean;
  /** For "almost full" scenario - remaining spots */
  remainingSpots?: number;
  maxAttendees?: number;
}

const TEST_SCENARIOS: TestScenario[] = [
  {
    id: "workshop-not-registered",
    label: "Workshop - Not Registered",
    sessionFormat: "workshop",
    isRegistered: false,
    userIsVip: false,
    hasRecording: false,
    hasSpecialOffer: false,
    isAtCapacity: false,
    limitReached: false,
  },
  {
    id: "workshop-registered",
    label: "Workshop - Registered",
    sessionFormat: "workshop",
    isRegistered: true,
    userIsVip: false,
    hasRecording: false,
    hasSpecialOffer: false,
    isAtCapacity: false,
    limitReached: false,
  },
  {
    id: "workshop-almost-full",
    label: "Workshop - Almost Full (3 spots left)",
    sessionFormat: "workshop",
    isRegistered: false,
    userIsVip: false,
    hasRecording: false,
    hasSpecialOffer: false,
    isAtCapacity: false,
    limitReached: false,
    remainingSpots: 3,
    maxAttendees: 100,
  },
  {
    id: "workshop-limit-reached",
    label: "Workshop - Limit Reached",
    sessionFormat: "workshop",
    isRegistered: false,
    userIsVip: false,
    hasRecording: false,
    hasSpecialOffer: false,
    isAtCapacity: false,
    limitReached: true,
  },
  {
    id: "workshop-at-capacity",
    label: "Workshop - At Capacity",
    sessionFormat: "workshop",
    isRegistered: false,
    userIsVip: false,
    hasRecording: false,
    hasSpecialOffer: false,
    isAtCapacity: true,
    limitReached: false,
  },
  {
    id: "presentation-not-registered",
    label: "Presentation - Not Registered",
    sessionFormat: "presentation",
    isRegistered: false,
    userIsVip: false,
    hasRecording: false,
    hasSpecialOffer: false,
    isAtCapacity: false,
    limitReached: false,
  },
  {
    id: "presentation-registered",
    label: "Presentation - Registered (reminder set)",
    sessionFormat: "presentation",
    isRegistered: true,
    userIsVip: false,
    hasRecording: false,
    hasSpecialOffer: false,
    isAtCapacity: false,
    limitReached: false,
  },
  {
    id: "with-special-offer",
    label: "Workshop - With Special Offer",
    sessionFormat: "workshop",
    isRegistered: true,
    userIsVip: false,
    hasRecording: false,
    hasSpecialOffer: true,
    isAtCapacity: false,
    limitReached: false,
  },
  {
    id: "recording-vip",
    label: "Ended - VIP with Recording",
    sessionFormat: "workshop",
    isRegistered: true,
    userIsVip: true,
    hasRecording: true,
    hasSpecialOffer: false,
    isAtCapacity: false,
    limitReached: false,
  },
  {
    id: "recording-non-vip",
    label: "Ended - Non-VIP (no recording access)",
    sessionFormat: "workshop",
    isRegistered: true,
    userIsVip: false,
    hasRecording: true,
    hasSpecialOffer: false,
    isAtCapacity: false,
    limitReached: false,
  },
  {
    id: "ended-with-special-offer",
    label: "Ended - Registered with Special Offer",
    sessionFormat: "workshop",
    isRegistered: true,
    userIsVip: false,
    hasRecording: false,
    hasSpecialOffer: true,
    isAtCapacity: false,
    limitReached: false,
  },
  {
    id: "presentation-ended",
    label: "Presentation - Ended (review available)",
    sessionFormat: "presentation",
    isRegistered: false,
    userIsVip: false,
    hasRecording: false,
    hasSpecialOffer: false,
    isAtCapacity: false,
    limitReached: false,
  },
];

function computePhase(
  offsetMinutes: number,
  durationMinutes: number,
): SessionPhase {
  if (offsetMinutes >= durationMinutes) return "ended";
  if (offsetMinutes >= 0) return "live";
  if (offsetMinutes >= -15) return "joinable";
  return "upcoming";
}

function buildActionState(
  scenario: TestScenario,
  offsetMinutes: number,
  durationMinutes: number,
): SessionActionState {
  const phase = computePhase(offsetMinutes, durationMinutes);
  const isPresentation = scenario.sessionFormat === "presentation";
  const isJoinWindow = phase === "joinable" || phase === "live";
  const liveUrl = "https://zoom.us/j/123456789";

  // For recording scenarios, override phase to ended
  const effectivePhase = scenario.hasRecording ? "ended" : phase;
  const effectiveSessionNotEnded = effectivePhase !== "ended";

  const canJoin =
    isJoinWindow &&
    !scenario.hasRecording &&
    (isPresentation || scenario.isRegistered);

  const canRegister =
    !scenario.isRegistered &&
    effectiveSessionNotEnded &&
    !scenario.isAtCapacity &&
    !scenario.limitReached &&
    !isPresentation &&
    !scenario.hasRecording;

  const canRegisterAndJoin =
    !scenario.isRegistered &&
    isJoinWindow &&
    !scenario.isAtCapacity &&
    !scenario.limitReached &&
    !isPresentation &&
    !scenario.hasRecording;

  const canGetReminder =
    isPresentation &&
    !scenario.isRegistered &&
    effectiveSessionNotEnded &&
    !scenario.hasRecording;

  // Special offer visible from 15 min before end (offset >= duration - 15)
  const specialOfferVisible =
    scenario.hasSpecialOffer && offsetMinutes >= durationMinutes - 15;

  // Can only cancel before session starts (not during live or after)
  const canCancelRegistration =
    scenario.isRegistered &&
    (phase === "upcoming" || phase === "joinable") &&
    !specialOfferVisible &&
    !scenario.hasRecording;

  const canWatchRecording = scenario.hasRecording && scenario.userIsVip;
  const showRecordingVipPrompt = scenario.hasRecording && !scenario.userIsVip;

  // Review link visible 15 min before end for registered users OR presentations
  const canShowReviewLink =
    (scenario.isRegistered || isPresentation) &&
    offsetMinutes >= durationMinutes - 15;

  // Calculate remaining spots
  const remainingSpots =
    scenario.remainingSpots ?? (scenario.isAtCapacity ? 0 : 50);
  const maxAttendees = scenario.maxAttendees ?? 200;
  const currentAttendees = maxAttendees - remainingSpots;

  // Show low capacity warning when <10% spots left
  const showLowCapacityWarning =
    effectiveSessionNotEnded &&
    remainingSpots > 0 &&
    remainingSpots / maxAttendees < 0.1 &&
    !scenario.isRegistered;

  return {
    phase: effectivePhase,
    isRegistered: scenario.isRegistered,
    isAtCapacity: scenario.isAtCapacity,
    remainingSpots,
    currentAttendees,
    maxAttendees,
    showLowCapacityWarning,
    showSpotsCount:
      !scenario.isRegistered &&
      !scenario.hasRecording &&
      currentAttendees >= 100,
    limitReached: scenario.limitReached,
    workshopLimit: scenario.limitReached ? { current: 3, max: 3 } : null,
    specialOfferVisible,
    canShowReviewLink,
    isPresentation,
    liveUrl: scenario.hasRecording ? null : liveUrl,
    recordingUrl: scenario.hasRecording
      ? "https://example.com/recording"
      : null,
    actions: {
      canJoin,
      canRegister,
      canRegisterAndJoin,
      canGetReminder,
      canCancelRegistration,
      canWatchRecording,
      showRecordingVipPrompt,
    },
  };
}

function DebugPage() {
  const [offsetMinutes, setOffsetMinutes] = useState(-120);
  const [durationMinutes] = useState(60);

  const phase = computePhase(offsetMinutes, durationMinutes);

  const mockSession: SummitSession = useMemo(
    () => ({
      id: "debug-session",
      title: "Debug Test Session",
      description: "A test session for debugging button states",
      speaker: {
        id: "speaker-1",
        name: "Test Speaker",
        title: "Test Title",
        avatarUrl: null,
      },
      startTimeUtc: subMinutes(new Date(), offsetMinutes),
      durationMinutes,
      date: new Date(),
      startTime: "10:00 AM",
      endTime: "11:00 AM",
      sessionStartUtc: new Date().toISOString(),
      topics: ["Testing"],
      isFoundingCoach: false,
      isFeaturedCoach: false,
      summitOffer: null,
    }),
    [offsetMinutes, durationMinutes],
  );

  const mockSpecialOffer = {
    id: "offer-1",
    headline: "Special Offer",
    directSaleEnabled: true,
    price: 100,
    currency: "USD",
    coachId: "coach-1",
    coachUsername: "testcoach",
    applicationDeadline: null,
    cohortStartDate: null,
    isFoundingCoach: false,
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="mx-auto max-w-6xl space-y-8">
        <div>
          <h1 className="text-3xl font-bold">Session Button Debug</h1>
          <p className="mt-2 text-gray-600">
            Test all session action button states with time simulation
          </p>
        </div>

        {/* Time Controls */}
        <Card>
          <CardHeader>
            <CardTitle>Time Simulation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Time offset from session start</Label>
                <Badge
                  variant={
                    phase === "ended"
                      ? "secondary"
                      : phase === "live"
                        ? "default"
                        : phase === "joinable"
                          ? "outline"
                          : "secondary"
                  }
                >
                  Phase: {phase}
                </Badge>
              </div>
              <Slider
                value={[offsetMinutes]}
                onValueChange={([value]) => setOffsetMinutes(value)}
                min={-120}
                max={120}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>2h before</span>
                <span>Start</span>
                <span>2h after</span>
              </div>
              <p className="text-sm text-gray-600">
                Current offset:{" "}
                <strong>
                  {offsetMinutes > 0 ? "+" : ""}
                  {offsetMinutes} minutes
                </strong>{" "}
                from session start
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {TIME_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant={
                    offsetMinutes === preset.minutes ? "primary" : "default"
                  }
                  size="sm"
                  onClick={() => setOffsetMinutes(preset.minutes)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Test Scenarios Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {TEST_SCENARIOS.map((scenario) => {
            const state = buildActionState(
              scenario,
              offsetMinutes,
              durationMinutes,
            );
            const sessionWithOffer = scenario.hasSpecialOffer
              ? { ...mockSession, summitOffer: mockSpecialOffer }
              : mockSession;

            return (
              <Card key={scenario.id}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">
                    {scenario.label}
                  </CardTitle>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="outline" size="sm">
                      {scenario.sessionFormat}
                    </Badge>
                    {scenario.isRegistered && (
                      <Badge variant="secondary" size="sm">
                        registered
                      </Badge>
                    )}
                    {scenario.userIsVip && (
                      <Badge variant="secondary" size="sm">
                        VIP
                      </Badge>
                    )}
                    {scenario.hasRecording && (
                      <Badge variant="secondary" size="sm">
                        has recording
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="rounded-lg border bg-white p-4">
                    <SessionActionButtons
                      state={state}
                      sessionFormat={scenario.sessionFormat}
                      summitOffer={sessionWithOffer.summitOffer}
                      onRegister={() => alert("Register clicked")}
                      onCancelRegistration={() =>
                        alert("Cancel registration clicked")
                      }
                      size="sm"
                    />
                    {!Object.values(state.actions).some(Boolean) &&
                      state.phase !== "ended" && (
                        <p className="text-xs text-gray-400 italic">
                          No actions available
                        </p>
                      )}
                  </div>

                  {/* Debug state display */}
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs text-gray-500">
                      View computed state
                    </summary>
                    <pre className="mt-2 overflow-auto rounded bg-gray-50 p-2 text-xs">
                      {JSON.stringify(state, null, 2)}
                    </pre>
                  </details>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Legend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Phase Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-gray-300" />
                <span>Upcoming (≤15min before)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-yellow-400" />
                <span>Joinable (15min before → start)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-green-500" />
                <span>Live (start → end)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-gray-500" />
                <span>Ended</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
